import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Heart, MapPin, Star, Trash2 } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { useFavorites } from '../contexts/FavoritesContext';
import { EmptyState } from './EmptyState';

export function FavoritesTab() {
  const { favorites, removeFavorite } = useFavorites();

  if (favorites.length === 0) {
    return (
      <EmptyState
        title="No favorites yet"
        description="You haven't saved any favorite dishes yet. Start exploring and save dishes you love to see them here."
        actionLabel="Explore Dishes"
        onAction={() => window.location.href = '/'}
      />
    );
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-muted-foreground">
          {favorites.length} saved {favorites.length === 1 ? 'dish' : 'dishes'}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-8">
        {favorites.map((dish, index) => (
          <motion.article
            key={dish.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="border border-border group hover:border-foreground transition-colors"
          >
            {/* Image */}
            <Link to={`/dish/${dish.id}`} className="block relative aspect-square overflow-hidden">
              <ImageWithFallback
                src={dish.image}
                alt={dish.dishName}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Rating Badge */}
              <div className="absolute top-4 right-4 bg-background px-3 py-1.5 flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 fill-current" />
                <span className="text-xs">{dish.rating}</span>
              </div>

              {/* Remove Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeFavorite(dish.id);
                }}
                className="absolute top-4 left-4 w-9 h-9 bg-background border border-border hover:border-red-600 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Link>

            {/* Content */}
            <div className="p-6">
              <h3 className="serif text-2xl mb-2 group-hover:text-muted-foreground transition-colors">
                {dish.dishName}
              </h3>
              <p className="text-sm mb-3">{dish.restaurant}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                <MapPin className="w-3.5 h-3.5" />
                <span>{dish.location}</span>
              </div>
              <div className="h-px bg-border mb-4"></div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Saved {dish.savedAt}</p>
                <Heart className="w-4 h-4 fill-current" />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  );
}

